import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { BaseDownloadServiceWithFilter } from './base.service';
import { Estimate } from '@cl/shared/models/estimates/estimate.model';
import { lastValueFrom } from 'rxjs';
import { MapImage } from '@cl/shared/components/map/map-image.model';
import { PrintoutDialogOptions } from '@cl/shared/components/printout-dialog/printout-dialog-options.model';
import { PrintoutDialogComponent } from '@cl/shared/components/printout-dialog/printout-dialog.component';
import { NzMessageService } from 'ng-zorro-antd/message';
import { LoadingService } from './loading.service';
import { FileMetadata } from '@cl/shared/models/file.model';
import { NzImageService } from 'ng-zorro-antd/image';
import { CommandResult, PrintoutType } from '@cl/shared/models';
import { EstimateFilter } from '@cl/shared/models/estimates/estimate-filter.model';
import { CadastreGeom } from '@cl/shared/models/cadastre-info/cadastre-geom.model';
import { BaseClientNotificationConfiguration } from '@cl/shared/models/base/base-client-notification-configuration.model';
import { DashboardEstimateStats } from '@cl/shared/models/estimates/dashboard-estimate-stats.model';
import { EstimatesStats } from '@cl/shared/models/estimates/estimates-stats-model';
import { NzModalService } from 'ng-zorro-antd/modal';

@Injectable({ providedIn: 'root' })
export class EstimateService extends BaseDownloadServiceWithFilter<Estimate, EstimateFilter> {
    constructor(
        httpClient: HttpClient,
        private loadingService: LoadingService,
        protected nzImageService: NzImageService,
        protected msgService: NzMessageService,
        protected modalService: NzModalService,
    ) {
        super(httpClient, '/api/v1/estimates/', nzImageService);
    }

    async downloadAttachment(estimateId: number, fileId: string, isMapImage: boolean = false): Promise<void> {
        return this.handleFileDownloading(
            lastValueFrom(this.httpClient.get<CommandResult<FileMetadata>>(`${this.baseApiUrl}${estimateId}/${isMapImage}/${fileId}`)),
        );
    }

    async deleteAttachment(estimateId: number, fileId: string, isMapImage: boolean = false): Promise<void> {
        return lastValueFrom(this.httpClient.delete<void>(`${this.baseApiUrl}${estimateId}/${isMapImage}/${fileId}`));
    }

    addAttachment(estimateId: number, formData: FormData): Promise<boolean> {
        return lastValueFrom(this.httpClient.post<boolean>(`${this.baseApiUrl}${estimateId}/attachments`, formData));
    }

    addMapImage(estimateId: number, mapImage: MapImage): Promise<boolean> {
        return lastValueFrom(this.httpClient.post<boolean>(`${this.baseApiUrl}${estimateId}/map-images`, mapImage));
    }

    toggleIncludeInPrintout(estimateId: number, attachmentId: number, includeInPrintout: boolean): Promise<boolean> {
        return lastValueFrom(this.httpClient.post<boolean>(`${this.baseApiUrl}${estimateId}/attachments/${attachmentId}/${includeInPrintout}`, null));
    }

    async downloadPrintout(estimateId: number, printoutOptions: PrintoutDialogOptions): Promise<string> {
        return this.handleFileDownloading(
            lastValueFrom(
                this.httpClient.get<CommandResult<FileMetadata>>(
                    `${this.baseApiUrl}${estimateId}/printout/${printoutOptions.userId}/${printoutOptions.printoutTemplateId}`,
                ),
            ),
        );
    }

    async print(estimate: Estimate): Promise<void> {
        const modal = this.modalService.create({
            nzTitle: 'Izdrukas iestatījumi',
            nzContent: PrintoutDialogComponent,
        });

        modal.afterOpen.subscribe(() => {
            const component = modal.getContentComponent();
            component.printoutType = PrintoutType.Estimate;
            component.orderTypeId = estimate.ordersTypeId;
            component.loadTemplates();
        });

        modal.afterClose.subscribe(async (printoutOptions: PrintoutDialogOptions) => {
            if (printoutOptions) {
                try {
                    this.loadingService.isLoading.next(true);
                    await this.downloadPrintout(estimate.id, printoutOptions);
                    this.loadingService.isLoading.next(false);
                } catch (ex) {
                    this.loadingService.isLoading.next(false);
                    console.error(ex);
                    if (ex.error?.errors) {
                        ex.error.errors.forEach((error) => {
                            this.msgService.error(error);
                        });
                        return;
                    }

                    this.msgService.error('Neizdevās lejupielādēt izdruku!');
                    return;
                }
            }
        });
    }

    async getCadastreGeomsByCadastreNumbers(cadastreNumbers: string): Promise<CommandResult<CadastreGeom[]>> {
        return lastValueFrom(this.httpClient.get<CommandResult<CadastreGeom[]>>(`${this.baseApiUrl}cadastre-geoms/${cadastreNumbers}`));
    }

    async sendEstimateNotification(estimateNotificationConfig: BaseClientNotificationConfiguration): Promise<void> {
        return await lastValueFrom(this.httpClient.post<void>(`${this.baseApiUrl}send-notification`, estimateNotificationConfig));
    }

    async getEstimatesStats(): Promise<CommandResult<EstimatesStats>> {
        return await lastValueFrom(this.httpClient.get<CommandResult<EstimatesStats>>(`${this.baseApiUrl}stats`));
    }

    async getEstimateStatsForDashboard(): Promise<CommandResult<DashboardEstimateStats>> {
        return await lastValueFrom(this.httpClient.get<CommandResult<DashboardEstimateStats>>(`${this.baseApiUrl}dashboard-stats`));
    }

    async getActiveEstimatesBySearchParams(searchParams: HttpParams): Promise<CommandResult<Estimate[]>> {
        return await lastValueFrom(this.httpClient.get<CommandResult<Estimate[]>>(`${this.baseApiUrl}active-estimates`, { params: searchParams }));
    }

    async copyEstimate(estimateId: number): Promise<CommandResult<number>> {
        return await lastValueFrom(this.httpClient.post<CommandResult<number>>(`${this.baseApiUrl}${estimateId}/copy`, null));
    }
}
